import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * A page's in-page section, kept in the URL hash.
 *
 * `useState('media')` is the right amount of machinery for a tab strip right up
 * until something outside the page needs to point AT one of its tabs. The
 * command search does: /settings has eight panels and /settings/advanced seven,
 * and none of them could be linked to — the page always opened on its first one
 * and you found the rest by reading the strip.
 *
 * So the hash carries it. Arriving with one opens that section; switching
 * section rewrites it, so the address bar always describes what is on screen and
 * the link can be shared or bookmarked. `replace`, because a tab strip is not
 * navigation: eight clicks around a settings page should not be eight presses of
 * the back button to leave it.
 *
 * An unknown hash is ignored rather than obeyed — `/settings#kpi` is a stale
 * link, not an eighth panel — and the page opens on its default.
 *
 * @param {string} fallback  the section shown when the hash says nothing
 * @param {string[]} ids     every section this page answers to
 * @returns {[string, (id: string) => void]}
 */
export default function useHashTab(fallback, ids) {
  const { hash, pathname, search } = useLocation()
  const navigate = useNavigate()

  const wanted = decodeURIComponent(hash.slice(1))
  const known = ids.includes(wanted)
  const [tab, setTab] = useState(() => (known ? wanted : fallback))

  /* A jump that lands while the page is ALREADY open — searching from
     /settings#media for the notifications panel never unmounts anything, so
     without this the URL would move and the panel would not. */
  useEffect(() => {
    if (known) setTab(wanted)
  }, [known, wanted])

  const go = (id) => {
    setTab(id)
    navigate(
      /* The default section leaves no hash behind it: `/settings` and
         `/settings#identity` are the same screen, and only one of them should
         be the address you copy. */
      { pathname, search, hash: id === fallback ? '' : `#${id}` },
      /* `tabWrite` is how the shell tells the two directions apart. Arriving
         with a hash means "take me there", and useHashPart scrolls to it;
         a hash the page wrote because you clicked one of its own tabs means
         you are already looking at it, and scrolling would yank the viewport
         out from under the click. */
      { replace: true, state: { tabWrite: true } },
    )
  }

  return [tab, go]
}
